import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal, useToast } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * InvoiceTermsDetail — /v2/invoice-terms/:id
 *
 * A focused fee-structure editor (Domain 04.2), replacing the generic detail
 * dump. Configure a client's arrangement once: contingency %, flat fee, or
 * hybrid (both), plus per-account (property_cap) and per-client (total_cap)
 * caps. `rate` is a decimal fraction in the DB (0.25 = 25%); the editor works
 * in whole percent and converts on read/write.
 */

type BillingType = 'contingency' | 'flat_fee' | 'hybrid' | 'reduction_contingency';

interface InvoiceTerm {
  id: string;
  client_id: string | null;
  property_id: string | null;
  year: number | null;
  name: string | null;
  billing_type: BillingType | null;
  rate: number | null;
  flat_amount: number | null;
  property_cap: number | null;
  total_cap: number | null;
}

interface ClientLite { id: string; name: string | null; short_name: string | null }

const billingTypeLabel: Record<BillingType, string> = {
  contingency: 'Contingency', flat_fee: 'Flat fee', hybrid: 'Hybrid',
  reduction_contingency: 'Reduction contingency',
};
// reduction_contingency bills a % of the raw value reduction (tax-rate-free), so it
// carries a rate just like plain contingency and hybrid.
const hasRate = (t: BillingType | null) =>
  t === 'contingency' || t === 'hybrid' || t === 'reduction_contingency';
const hasFlat = (t: BillingType | null) => t === 'flat_fee' || t === 'hybrid';

const money = (v: number | null) => (v != null ? `$${Math.round(v).toLocaleString()}` : '—');
const pct = (v: number | null) => (v != null ? `${+(v * 100).toFixed(4)}%` : '—');

interface RecomputeResult { term_id: string; recomputed: number; invoices_refreshed: number }

export function InvoiceTermsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useEntityPermission('invoice_term', 'can_edit');

  const termQuery = useQuery<InvoiceTerm>({
    queryKey: ['invoice_term', id],
    queryFn: ({ signal }) => api.get<InvoiceTerm>(`/api/invoice-terms/${id}`, signal),
    enabled: !!id,
  });

  // 04.6 — explicitly re-propagate this term's rate to its dependent staged
  // (draft/pending) fee items via the real cascade endpoint. Frozen (sent/paid)
  // items are never touched server-side.
  const recompute = useMutation<RecomputeResult>({
    mutationFn: () => api.post<RecomputeResult>(`/api/billing/recompute-contingency/${id}`),
    onSuccess: (r) => {
      toast({
        variant: 'success',
        message: r.recomputed > 0
          ? `Recomputed ${r.recomputed} staged fee${r.recomputed === 1 ? '' : 's'} across ${r.invoices_refreshed} draft invoice${r.invoices_refreshed === 1 ? '' : 's'}.`
          : 'No staged fees follow this term — nothing to recompute.',
      });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  const clientId = termQuery.data?.client_id ?? undefined;
  const clientQuery = useQuery<ClientLite>({
    queryKey: ['client', clientId],
    queryFn: ({ signal }) => api.get<ClientLite>(`/api/clients/${clientId}`, signal),
    enabled: !!clientId,
  });

  if (termQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading fee terms…</p>;
  if (termQuery.error) {
    const notFound = termQuery.error instanceof ApiClientError && termQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Fee terms not found' : 'Failed to load fee terms'}</h2>
        <p style={{ margin: 0 }}>{(termQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/invoice-terms')}>Back to Invoice Terms</Button>
        </p>
      </div>
    );
  }
  if (!termQuery.data) return null;

  const term = termQuery.data;
  const client = clientQuery.data;
  const clientName = client?.name || client?.short_name || null;

  return (
    <>
      <EntityHeader
        prefix="Fee terms"
        title={term.name || (term.billing_type ? `${billingTypeLabel[term.billing_type]} terms` : 'Fee terms')}
        subtitle={[clientName, term.year ? `Tax year ${term.year}` : null].filter(Boolean).join(' · ') || undefined}
        statusPills={term.billing_type ? <Badge variant="info" dot>{billingTypeLabel[term.billing_type]}</Badge> : undefined}
        actions={canEdit ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              loading={recompute.isPending}
              onClick={() => recompute.mutate()}
            >
              Recompute staged fees
            </Button>
            <Button variant="primary" size="sm" onClick={() => setEditOpen(true)}>Edit fee structure</Button>
          </>
        ) : undefined}
      />

      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-5)',
        maxWidth: 520,
      }}>
        <h3 style={{
          margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Fee structure</h3>
        <Row label="Billing type" value={term.billing_type ? billingTypeLabel[term.billing_type] : '—'} />
        {hasRate(term.billing_type) && <Row label="Contingency rate" value={pct(term.rate)} />}
        {hasFlat(term.billing_type) && <Row label="Flat fee" value={money(term.flat_amount)} />}
        <Row label="Per-account cap" value={money(term.property_cap)} />
        <Row label="Per-client cap" value={money(term.total_cap)} />
        {clientName && (
          <p style={{ margin: 'var(--sp-4) 0 0', fontSize: 'var(--fs-sm)' }}>
            <a
              onClick={() => navigate(`/clients/${term.client_id}`)}
              style={{ color: 'var(--brand-primary)', cursor: 'pointer' }}
            >View {clientName}</a>
          </p>
        )}
      </div>

      {editOpen && (
        <EditFeeStructure
          term={term}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

function EditFeeStructure({ term, onClose }: { term: InvoiceTerm; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [billingType, setBillingType] = useState<BillingType>(term.billing_type || 'contingency');
  const [ratePct, setRatePct] = useState(term.rate != null ? String(+(term.rate * 100).toFixed(4)) : '');
  const [flat, setFlat] = useState(term.flat_amount != null ? String(term.flat_amount) : '');
  const [propertyCap, setPropertyCap] = useState(term.property_cap != null ? String(term.property_cap) : '');
  const [totalCap, setTotalCap] = useState(term.total_cap != null ? String(term.total_cap) : '');

  const num = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        billing_type: billingType,
        // Only persist the field(s) the mode uses; clear the other so a switched
        // mode can't leave a stale rate/flat behind.
        rate: hasRate(billingType) ? (num(ratePct) != null ? (num(ratePct) as number) / 100 : null) : null,
        flat_amount: hasFlat(billingType) ? num(flat) : null,
        property_cap: num(propertyCap),
        total_cap: num(totalCap),
      };
      return api.put(`/api/invoice-terms/${term.id}`, body);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: 'Fee structure saved.' });
      queryClient.invalidateQueries({ queryKey: ['invoice_term', term.id] });
      onClose();
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  return (
    <Modal open onClose={onClose} title="Edit fee structure" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <Field label="Billing type">
          <Select
            value={billingType}
            onChange={(e) => setBillingType(e.target.value as BillingType)}
            style={{ width: '100%' }}
            options={[
              { value: 'contingency', label: 'Contingency (% of tax saved)' },
              { value: 'reduction_contingency', label: 'Reduction contingency (% of value reduction)' },
              { value: 'flat_fee', label: 'Flat fee' },
              { value: 'hybrid', label: 'Hybrid (flat + %)' },
            ]}
          />
        </Field>
        {hasRate(billingType) && (
          <Field label="Contingency rate (%)">
            <Input type="number" value={ratePct} onChange={(e) => setRatePct(e.target.value)} placeholder="e.g. 25" style={{ width: '100%' }} />
          </Field>
        )}
        {hasFlat(billingType) && (
          <Field label="Flat fee ($)">
            <Input type="number" value={flat} onChange={(e) => setFlat(e.target.value)} placeholder="e.g. 1200" style={{ width: '100%' }} />
          </Field>
        )}
        <Field label="Per-account cap ($, optional)">
          <Input type="number" value={propertyCap} onChange={(e) => setPropertyCap(e.target.value)} placeholder="No cap" style={{ width: '100%' }} />
        </Field>
        <Field label="Per-client cap ($, optional)">
          <Input type="number" value={totalCap} onChange={(e) => setTotalCap(e.target.value)} placeholder="No cap" style={{ width: '100%' }} />
        </Field>
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Changing the rate recomputes staged billable items for this client (contingency cascade).
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)' }}>{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)',
      padding: 'var(--sp-2) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
